import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAttachSurveyToGroupMutation,
  useEditSurveyAssignmentMutation,
  useRemoveSurveyAssignmentMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { surveyErrorMessage } from './surveyModel';

interface IAttachSurveyArgs {
  surveyId: string;
  groupId: string;
  triggerLessonId: string;
}

interface IAttachResult {
  success: boolean;
  /** Localized DUPLICATE_ASSIGNMENT / COURSE_MISMATCH / … for inline display. */
  errorMessage?: string;
}

interface ISurveyAssignmentMutationsHook {
  /** Attach a template to a group lesson; errors return inline (popup form). */
  attach: (args: IAttachSurveyArgs) => Promise<IAttachResult>;
  /** Toggle an assignment on/off (LMS visibility). */
  setActive: (id: string, isActive: boolean) => Promise<boolean>;
  /** Delete an assignment; HAS_RESPONSES surfaces the explicit toast. */
  remove: (id: string) => Promise<boolean>;
  pending: boolean;
}

/**
 * Spec 0011 — assignment lifecycle (`assignSurveyToGroup` /
 * `updateSurveyAssignment` / `deleteSurveyAssignment`). `attach` refetches the
 * active `ErpGroupSurveyAssignments` query so the group tab updates the moment
 * the popup closes; it reports failures inline (the popup renders them as a
 * form-level error) while `setActive`/`remove` follow the fire-and-toast
 * house pattern.
 */
export const useSurveyAssignmentMutations = (
  onChanged?: () => void,
): ISurveyAssignmentMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [attachRun] = useAttachSurveyToGroupMutation({
    // ErpGroupSurveyAssignments → the group card's Surveys tab;
    // ErpSurveysList → the /surveys list «Прикреплений» counter (attach from
    // the list). Apollo refetches only whichever is currently mounted.
    refetchQueries: ['ErpGroupSurveyAssignments', 'ErpSurveysList'],
  });
  const [editRun] = useEditSurveyAssignmentMutation();
  const [removeRun] = useRemoveSurveyAssignmentMutation();
  const [pending, setPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.Survey,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const attach: ISurveyAssignmentMutationsHook['attach'] = useCallback(
    async ({ surveyId, groupId, triggerLessonId }) => {
      setPending(true);
      try {
        const result = await attachRun({
          variables: { surveyId, groupId, triggerLessonId },
        });
        const payload = result.data?.assignSurveyToGroup;
        if (payload?.success && payload.assignment) {
          onChanged?.();
          return { success: true };
        }
        return {
          success: false,
          errorMessage: surveyErrorMessage(t, payload?.customErrors),
        };
      } catch (e) {
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setPending(false);
      }
    },
    [attachRun, onChanged, t],
  );

  const setActive: ISurveyAssignmentMutationsHook['setActive'] = useCallback(
    async (id, isActive) => {
      setPending(true);
      try {
        const result = await editRun({ variables: { id, isActive } });
        const payload = result.data?.updateSurveyAssignment;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.surveys.groupTab.toastToggleError'),
          surveyErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.surveys.groupTab.toastToggleError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [editRun, onChanged, toastError, t],
  );

  const remove: ISurveyAssignmentMutationsHook['remove'] = useCallback(
    async (id) => {
      setPending(true);
      try {
        const result = await removeRun({ variables: { id } });
        const payload = result.data?.deleteSurveyAssignment;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        const code = (payload?.customErrors || []).find(Boolean)?.code;
        // «есть ответы — деактивируйте» — the explicit HAS_RESPONSES hint.
        toastError(
          code === 'HAS_RESPONSES'
            ? t('pages.surveys.errors.HAS_RESPONSES')
            : t('pages.surveys.groupTab.toastDeleteError'),
          code === 'HAS_RESPONSES'
            ? undefined
            : surveyErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.surveys.groupTab.toastDeleteError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [removeRun, onChanged, toastError, t],
  );

  return { attach, setActive, remove, pending };
};
